import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";

import {
    Container
} from "./style";
import api from "../../services/api";
import { Panel } from "../../interfaces/Panel";
import { useToast } from "../../hooks/toast";
import DashboardManagerBar from "./DashboardManagerBar";
import MenuPage from "./MenuPage";
import { FilterConditionItem, FilterFlowProps } from "../FilterBar/FilterDialog";
import { Flow } from "../../interfaces/Flow";
import { ElementProps } from "../ElementBuilder";
import NewElement from "../../dialogs/NewElement";
import Dashboard from "../Dashboard";

interface DashboardManagerProps {
    typeUser: string;
    flow_id: number;
    testModelFlow?: boolean;
    printComponentRef?: React.MutableRefObject<null>;
}

const DashboardManager: React.FC<DashboardManagerProps> = ({ typeUser, flow_id, testModelFlow, printComponentRef }) => {

    const { addToast } = useToast();

    const [panels, setPanels] = useState<Panel[]>([]);
    const [panel, setPanel] = useState<Panel>();
    const [flow, setFlow] = useState<Flow>();
    const [openPagesMenu, setOpenPagesMenu] = useState<boolean>(false);
    const [elementSelected, setElementSelected] = useState<ElementProps | undefined>(undefined);
    const [pendingSavingPanel, setPendingSavingPanel] = useState<boolean>(false);
    const [openNewElement, setOpenNewElement] = useState(false);
    const [statusSavedPanel, setStatusSavedPanel] = useState<number>();
    const [loadingPanel, setLoadingPanel] = useState<boolean>(false);
    const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

    // Stores the JSON string of the last applied filter for quick comparison
    const [filterConditions, setFilterConditions] = useState<FilterConditionItem[] | undefined>(undefined);
    const prevFilterConditionsStringRef = useRef<string | undefined>();
    const prevPanelBaseRef = useRef<string | undefined>();
    const prevPanelIdRef = useRef<number | undefined>();

    // Creates a memoized filter flow
    const filterFlow: FilterFlowProps = useMemo(() => {

        const conditions = panel?.schema_view !== undefined ? JSON.parse(panel.schema_view) : undefined;

        return {
            conditions: conditions || []
        }
    }, [panel?.schema_view]);

    const panelId = panel?.id_panel;

    const getApiPanelData = useCallback(async (idPanel?: number, conditions?: FilterConditionItem[]) => {

        if ((panelId !== undefined || idPanel !== undefined) && flow !== undefined && flow.id_flow !== undefined) {

            setLoadingPanel(true);

            await api.post(`/panel/data/v2`, {
                id_panel: idPanel !== undefined ? idPanel : panelId,
                flow_id: flow.id_flow,
                isTestMode: testModelFlow,
                conditions: conditions !== undefined ? conditions : undefined
            }).then(response => {
                if (response.data !== null) {

                    const respPanel: Panel = response.data;

                    if (respPanel !== undefined) {
                        setPanel((prev) => {
                            if (!prev) return prev;

                            const newElements = prev.elements?.map((e) => {
                                const arrData = respPanel.elements?.filter((elF) => elF.hash === e.hash);
                                if (arrData && arrData.length > 0) {
                                    return { ...e, dataElement: arrData[0].dataElement };
                                }
                                return e;
                            });

                            return { ...prev, elements: newElements, pendingUpd: false, pendingUpdData: false } as Panel;
                        });
                    }

                }
                setLoadingPanel(false);
            }).catch(error => {
                setLoadingPanel(false);
                addToast({
                    type: 'error',
                    title: 'Erro ao carregar os dados do Painel',
                    description: 'Ocorreu ao tentar carregar os dados do Painel!',
                });
            });

        }

    }, [addToast, flow, testModelFlow, panelId]);

    const getPanelsApi = useCallback(async (selectId?: number) => {

        if (flow_id !== undefined && flow_id > 0) {

            api.get(`/panel/by-flow`, {
                params: {
                    flow_id: flow_id
                }
            }).then(response => {
                if (response.data !== null) {

                    const respPanels: Panel[] = response.data;

                    if (respPanels !== undefined) {
                        // Sort panels alphabetically
                        const sortedPanels = respPanels.sort((a, b) => a.name.localeCompare(b.name, 'pt', { sensitivity: 'base' }));

                        setPanels(sortedPanels);

                        // Sets the selected panel
                        if (selectId !== undefined && selectId > 0) {
                            const selected = sortedPanels.find(p => p.id_panel === selectId);
                            if (selected) {
                                setPanel(selected);
                            } else if (sortedPanels.length > 0) {
                                setPanel(sortedPanels[0]);
                            }
                        } else if (sortedPanels.length > 0) {
                            setPanel(sortedPanels[0]);
                        }
                    }

                }

            }).catch(error => {
                addToast({
                    type: 'error',
                    title: 'Erro ao carregar os Painéis do Fluxo',
                    description: 'Ocorreu ao tentar abrir os painéis do fluxo!',
                });
            });

        }

    }, [addToast, flow_id]);

    const getApiFlow = useCallback(async () => {

        await api.get(`/flow`, {
            params: {
                id_flow: flow_id,
                withSteps: true,
                withCards: false,
                removeStepsCondicionals: true
            }
        }).then(response => {
            if (response.data !== null) {

                const respFlow: Flow = response.data;

                if (respFlow !== undefined) {

                    setFlow(respFlow);

                }
            }
        });

    }, [flow_id]);

    const getApiPanel = useCallback(async (id_panel: number) => {

        if (flow !== undefined && flow.id_flow > 0) {

            api.get(`/panel`, {
                params: {
                    flow_id: flow.id_flow,
                    id_panel: id_panel
                }
            }).then(response => {
                if (response.data !== null) {

                    const respPanel: Panel = response.data;

                    if (respPanel !== undefined) {
                        setPanel(respPanel);
                    }

                }

            }).catch(error => {
                addToast({
                    type: 'error',
                    title: 'Erro ao carregar o Painel do Fluxo',
                    description: 'Ocorreu ao tentar abrir o painel do Fluxo!',
                });
            });

        }

    }, [addToast, flow]);

    const handleRefreshPanels = useCallback((selectId?: number) => {
        getPanelsApi(selectId);
    }, [getPanelsApi]);

    const onFilterFlow = useCallback((filterFlow: FilterFlowProps, openedDialog: boolean) => {

        if (!openedDialog) {
            const newConditions = filterFlow?.conditions;

            // Convert to string for straightforward comparison
            const newConditionsString = JSON.stringify(newConditions ?? []);

            if (prevFilterConditionsStringRef.current !== newConditionsString) {
                prevFilterConditionsStringRef.current = newConditionsString;
                setFilterConditions(newConditions);
            }
        }

    }, [setFilterConditions]);

    const onAddElementDialog = useCallback(() => {
        setElementSelected(undefined);
        setOpenNewElement(true);
    }, []);

    const onUpdateStatusSavedPanel = useCallback(async (status: number) => {

        setStatusSavedPanel(status);

    }, []);

    const savePanel = useCallback(async (panelBase?: Panel, withLoadData?: boolean) => {

        onUpdateStatusSavedPanel(3);

        let panelToSave = panelBase !== undefined ? panelBase : panel;

        if (panelToSave !== undefined) {

            await api
                .post('/panel', panelToSave)
                .then(response => {

                    // Clears update flags to avoid resending in a loop
                    setPanel((prev) => prev ? { ...prev, pendingUpd: false, pendingUpdData: false } : prev);

                    setPendingSavingPanel(false);
                    onUpdateStatusSavedPanel(1);

                    if (panelToSave !== undefined && panelToSave.id_panel !== undefined) {
                        if (withLoadData) {
                            // Refresh panel structure if needed (e.g., IDs of newly created elements)
                            getApiPanel(panelToSave.id_panel);
                        }
                    }

                }).catch(error => {

                    setPendingSavingPanel(false);
                    onUpdateStatusSavedPanel(1);

                    addToast({
                        type: 'error',
                        title: 'Erro ao inserir um novo item [1]',
                        description: 'Ocorreu um erro ao inserir o registro!',
                    });
                });
        }

    }, [addToast, onUpdateStatusSavedPanel, getApiPanel, panel]);


    const onAddNewElement = useCallback(async (element: ElementProps | undefined) => {

        if (element === undefined) {
            setElementSelected(undefined);
        }

        if (panel !== undefined && element !== undefined) {

            let isEditing = false;

            // Check if the element already exists
            if (panel.elements !== undefined && panel.elements !== null && panel.elements.length > 0) {
                const arrElements = panel.elements.find((el) => el.hash === element.hash);

                if (arrElements !== undefined) {
                    isEditing = true;
                }
            }

            if (isEditing) {

                let newPanel = panel;

                let newArrElements = panel.elements !== undefined ? panel.elements : [];

                newArrElements = newArrElements.map((el) => {
                    if (el.hash === element.hash) {
                        return element;
                    } else {
                        return el;
                    }
                });

                newPanel.elements = newArrElements;
                newPanel.pendingUpd = true;

                setPanel(newPanel);

                savePanel(newPanel, true);

            } else {

                let newArrElements = panel.elements !== undefined ? panel.elements : [];

                newArrElements.push(element);

                setPanel(panel => {

                    if (panel !== undefined) {
                        return {
                            ...panel,
                            pendingUpd: true,
                            pendingUpdData: true,
                            elements: newArrElements
                        }
                    } else {
                        return undefined;
                    }

                });

            }

            setOpenNewElement(false);

        }

    }, [panel, savePanel]);

    const onCopyElement = useCallback(async (element: ElementProps) => {

        // Create an element hash
        const dateNowTime = new Date().getTime();
        var hashLib = require('object-hash');

        const newHash = await hashLib(dateNowTime + element.type + flow?.id_flow + "copy");

        let newElement: ElementProps = {
            hash: newHash,
            type: element.type,
            title: String("Cópia de " + element.title).substring(0, 44),
            help_text: element.help_text,
            width: element.width,
            height: element.height,
            pos_axis_y: element.pos_axis_y !== undefined ? element.pos_axis_y : 0,
            pos_axis_x: element.pos_axis_x !== undefined ? element.pos_axis_x : 0,
            axis_x_field_id: element.axis_x_field_id,
            axis_y_field_id: element.axis_y_field_id,
            calc_function: element.calc_function,
            sort_by: element.sort_by,
            embed: element.embed,
            axis_x_field_type: element.axis_x_field_type,
            axis_y_field_type: element.axis_y_field_type,
            group_by_field_id: element.group_by_field_id,
            group_by_field_type: element.group_by_field_type,
            group_by_field_date: element.group_by_field_date,
            show_time: element.show_time,
            group_date: element.group_date,
            color: element.color,
            filter: element.filter,
        };

        onAddNewElement(newElement);

    }, [flow, onAddNewElement]);

    const onEditElement = useCallback(async (element: ElementProps) => {

        setElementSelected(element);
        setOpenNewElement(true);

    }, []);

    const handleRefreshPanel = useCallback(() => {
        if (panel !== undefined && panel.id_panel !== undefined) {
            getApiPanelData(panel?.id_panel, panel?.schema_view !== undefined ? JSON.parse(panel.schema_view) : undefined);
        }
    }, [getApiPanelData, panel]);

    const onUpdatePanel = useCallback(async (panel: Panel) => {

        setPanel(panel);

    }, []);

    const refreshPanel = useCallback(async () => {
        if (panelId !== undefined) {
            getApiPanel(panelId);
        }
    }, [getApiPanel, panelId]);

    const handleSelectPanel = useCallback((panel: Panel) => {

        // Save the current panel before switching
        savePanel();

        // Reset references and filters for the new panel
        prevPanelIdRef.current = panel.id_panel;
        prevPanelBaseRef.current = undefined;

        // Update filters to reflect the selected panel schema_view
        setFilterConditions(undefined);
        prevFilterConditionsStringRef.current = undefined;

        if (panel.id_panel !== undefined) {
            getApiPanel(panel.id_panel);
        }

    }, [savePanel, setFilterConditions, getApiPanel]);

    useEffect(() => {

        if (filterConditions === undefined) return;

        // Update schema_view and persist if necessary
        if (panel !== undefined) {

            const schemaString = JSON.stringify(filterConditions);

            if (schemaString !== panel.schema_view) {

                let newFilterConditions = filterConditions;

                newFilterConditions = newFilterConditions.map((condition) => {
                    condition.fields = [];
                    condition.comparators = [];

                    return condition;
                });

                const updatedPanel: Panel = {
                    ...panel,
                    schema_view: JSON.stringify(newFilterConditions),
                    pendingUpd: true,
                    pendingUpdData: false,
                };

                setPanel(updatedPanel);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterConditions]);

    useEffect(() => {
        getPanelsApi();
        getApiFlow();
    }, [getPanelsApi, getApiFlow]);

    useEffect(() => {

        if (loadingPanel) return;

        let needRefresh = false;

        // Checks whether the panel already has data to avoid unnecessary refreshes
        if (panel !== undefined && panel.elements !== undefined && panel.elements.length > 0) {
            // Iterate through elements and check if they already have data
            panel.elements.forEach(element => {
                if (element.dataElement === undefined || element.dataElement === null) {
                    needRefresh = true;
                }
            });
        }

        // Reloads all element data to reflect new elements or changes
        if (!isFirstLoad && prevPanelBaseRef.current !== JSON.stringify(panel) && panel !== undefined && panel.id_panel !== undefined) {

            const prevPanelElements = JSON.parse(prevPanelBaseRef.current || '{}');

            if (prevPanelElements !== undefined && prevPanelElements.elements !== undefined && prevPanelElements.elements.length !== panel.elements?.length) {
                needRefresh = true;
            } else if (panel.pendingUpdData) {
                needRefresh = true;
            } else if (prevPanelBaseRef.current !== undefined && JSON.stringify(JSON.parse(prevPanelBaseRef.current).schema_view) !== JSON.stringify(panel.schema_view)) {
                needRefresh = true;
                if (!pendingSavingPanel) {
                    savePanel();
                }
            }

            prevPanelBaseRef.current = JSON.stringify(panel);

        } else if (isFirstLoad) {
            needRefresh = true;
            setIsFirstLoad(false);
        }

        if (needRefresh) {
            getApiPanelData(panel?.id_panel, panel?.schema_view !== undefined ? JSON.parse(panel.schema_view) : undefined);
        }

    }, [panel, getApiPanelData, isFirstLoad, savePanel, pendingSavingPanel, loadingPanel]);

    return (
        <Container>

            <DashboardManagerBar
                typeUser={typeUser}
                flow={flow}
                openPagesMenu={openPagesMenu}
                selectedPanel={panel}
                loadingPanel={loadingPanel}
                statusSaved={statusSavedPanel}
                filterFlow={filterFlow}
                setOpenPagesMenu={setOpenPagesMenu}
                refreshPanel={handleRefreshPanel}
                onFilterFlow={onFilterFlow}
                onAddElement={onAddElementDialog}
            />

            {openPagesMenu && (
                <MenuPage
                    flow_id={flow_id}
                    open={openPagesMenu}
                    setOpen={setOpenPagesMenu}
                    panels={panels}
                    selectedPanel={panel}
                    setSelectedPanel={handleSelectPanel}
                    refreshPanels={handleRefreshPanels}
                />
            )}

            {panel !== undefined && flow !== undefined && (
                <Dashboard
                    flow={flow}
                    panel={panel}
                    loading={loadingPanel}
                    typeUser={flow !== undefined && flow.typeUserAccess !== undefined ? flow.typeUserAccess : ""}
                    onUpdatePanel={onUpdatePanel}
                    onUpdateStatusSavedPanel={onUpdateStatusSavedPanel}
                    onCopyElement={onCopyElement}
                    onEditElement={onEditElement}
                    loadPanel={refreshPanel}
                    onSavePanel={savePanel}
                    printComponentRef={printComponentRef}
                    setPendingSaving={setPendingSavingPanel}
                    pendingSaving={pendingSavingPanel}
                />
            )}

            {openNewElement && flow !== undefined && (
                <NewElement
                    flow={flow}
                    open={openNewElement}
                    isTestMode={false}
                    onClose={() => {
                        setOpenNewElement(false);
                        setElementSelected(undefined);
                    }}
                    onAddNewElement={onAddNewElement}
                    element={elementSelected}
                />
            )}
        </Container>
    );

}

export default DashboardManager;